import { useEffect, useState, useContext, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, GroupContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, Save, Trash2, History, Clock, CheckCircle, RefreshCw, Archive, Edit, X, ChevronDown, ChevronRight, Link2, Plus, Unlink, AlertTriangle, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to convert any date format to YYYY-MM-DD for HTML date input
const toInputDateFormat = (dateStr) => {
  if (!dateStr) return '';
  
  // If already in ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.split('T')[0];
  }
  
  // Try to parse DD/MM/YYYY format
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  // Try to parse MM/DD/YYYY format (US)
  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('-');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  // Try generic Date parsing as fallback
  try {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  } catch (e) {
    // Parsing failed
  }
  
  return '';
};

// Helper function to format date for display
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return 'Not set';
  
  // Try to parse the date
  let date;
  
  // If DD/MM/YYYY format
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('/');
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(dateStr);
  }
  
  if (isNaN(date.getTime())) return dateStr; // Return original if can't parse
  
  return date.toLocaleDateString('en-GB');
};

// Helper function to format user display with organization tag
const formatUserDisplay = (user) => {
  if (!user) return 'Unassigned';
  const name = user.name || 'Unknown';
  
  // Use display_group if available (from /users/with-groups endpoint)
  if (user.display_group) {
    return `${name} (${user.display_group})`;
  }
  
  if (user.role === 'external_org' && user.organization) {
    return `${name} (${user.organization})`;
  } else if (user.organization && user.organization !== 'HLRA') {
    return `${name} (${user.organization})`;
  } else {
    return name;
  }
};

// Get organization badge color
const getOrgBadgeColor = (user) => {
  if (!user) return '';
  if (user.display_group_color) {
    return ''; // Will use inline style
  }
  if (user.role === 'external_org' || (user.organization && user.organization !== 'HLRA')) {
    return 'text-purple-600';
  }
  return 'text-blue-600';
};

export default function ActionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { activeGroup } = useContext(GroupContext);
  const [action, setAction] = useState(null);
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  
  // Filter users to only show members of the action's group
  const groupUsers = useMemo(() => {
    if (!action || !users.length) return users;
    
    // Get the action's group_id, fallback to active group for legacy actions
    const actionGroupId = action.group_id || activeGroup?.id;
    
    // If no group context available, return all users
    if (!actionGroupId) return users;
    
    return users.filter(user => {
      // Super admins can always be assigned
      if (user.role === 'super_admin' || user.is_platform_admin) return true;
      
      // Check if user has membership in the action's group
      const memberships = user.group_memberships || [];
      return memberships.some(m => m.group_id === actionGroupId);
    });
  }, [users, action, activeGroup]);
  
  // Change reason prompts state
  const [originalAction, setOriginalAction] = useState(null); // Track original values
  const [showAssigneeChangeDialog, setShowAssigneeChangeDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showDueDateChangeDialog, setShowDueDateChangeDialog] = useState(false);
  const [showDeferralDialog, setShowDeferralDialog] = useState(false); // Deferral modal
  const [assigneeChangeReason, setAssigneeChangeReason] = useState('');
  const [completionReason, setCompletionReason] = useState('');
  const [dueDateChangeReason, setDueDateChangeReason] = useState('');
  const [deferredUntil, setDeferredUntil] = useState(''); // Review date for deferral
  const [deferralReason, setDeferralReason] = useState(''); // Reason for deferral
  const [pendingUpdate, setPendingUpdate] = useState(null); // Store pending changes
  
  // Linked actions state
  const [linkedActions, setLinkedActions] = useState([]);
  const [linksOpen, setLinksOpen] = useState(true);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showCreateLinkedDialog, setShowCreateLinkedDialog] = useState(false);
  const [allActions, setAllActions] = useState([]);
  const [linkSearchTerm, setLinkSearchTerm] = useState('');
  const [selectedLinkType, setSelectedLinkType] = useState('related');
  const [selectedActionToLink, setSelectedActionToLink] = useState(null);
  const [newLinkedAction, setNewLinkedAction] = useState({
    title: '',
    description: '',
    owner_id: '',
    priority: 'Medium',
    due_date: '',
    note: ''
  });

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  // All users who can view an action can also edit it (including external users)
  const canEditAction = isAdmin || user?.role === 'committee' || user?.role === 'external_org';
  // Only admin/committee can archive
  const canArchive = isAdmin || user?.role === 'committee';

  useEffect(() => {
    fetchActionDetail();
    fetchNotes();
    fetchUsers();
    fetchCategories();
    fetchAuditLog();
    fetchLinkedActions();
  }, [id]);

  const fetchActionDetail = async () => {
    try {
      const response = await axios.get(`${API}/actions/${id}`);
      setAction(response.data);
      setOriginalAction(response.data); // Store original for comparison
    } catch (error) {
      toast.error('Failed to fetch action details');
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedActions = async () => {
    try {
      const response = await axios.get(`${API}/actions/${id}/links`);
      setLinkedActions(response.data);
    } catch (error) {
      console.error('Failed to fetch linked actions:', error);
    }
  };

  const fetchAllActions = async () => {
    try {
      const response = await axios.get(`${API}/actions`);
      // Filter out current action and already linked actions
      const linkedIds = linkedActions.map(l => l.action_id);
      const filtered = response.data.filter(a => a.id !== id && !linkedIds.includes(a.id) && !a.is_archived);
      setAllActions(filtered);
    } catch (error) {
      console.error('Failed to fetch actions:', error);
    }
  };

  const fetchAuditLog = async () => {
    try {
      const response = await axios.get(`${API}/actions/${id}/audit-log`);
      setAuditLog(response.data);
    } catch (error) {
      console.error('Failed to fetch audit log:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/actions/${id}/notes`);
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/with-groups`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleUpdate = async () => {
    // Check if assignee changed
    const assigneeChanged = originalAction && action.owner_id !== originalAction.owner_id;
    // Check if status changed to Completed
    const statusChangedToCompleted = originalAction && 
      action.status === 'Completed' && 
      originalAction.status !== 'Completed';
    // Check if status changed to Deferred
    const statusChangedToDeferred = originalAction && 
      action.status === 'Deferred' && 
      originalAction.status !== 'Deferred';
    // Check if due date changed
    const dueDateChanged = originalAction && action.due_date !== originalAction.due_date;
    
    // If assignee changed, prompt for reason
    if (assigneeChanged && !assigneeChangeReason) {
      setPendingUpdate(action);
      setShowAssigneeChangeDialog(true);
      return;
    }
    
    // If due date changed, prompt for reason
    if (dueDateChanged && !dueDateChangeReason) {
      setPendingUpdate(action);
      setShowDueDateChangeDialog(true);
      return;
    }
    
    // If status changed to Deferred, prompt for review date
    if (statusChangedToDeferred && !deferredUntil) {
      setPendingUpdate(action);
      setShowDeferralDialog(true);
      return;
    }
    
    // If status changed to Completed, prompt for reason
    if (statusChangedToCompleted && !completionReason) {
      setPendingUpdate(action);
      setShowCompletionDialog(true);
      return;
    }
    
    // Proceed with update
    await performUpdate(action, assigneeChangeReason, completionReason, dueDateChangeReason, deferredUntil, deferralReason);
  };

  const performUpdate = async (actionData, assigneeReason = '', completionReasonText = '', dueDateReason = '', deferredUntilDate = '', deferralReasonText = '') => {
    try {
      // Include reasons in the update payload
      // For deferred_until: use the dialog value if set, otherwise keep actionData's value
      const updatePayload = {
        ...actionData,
        assignee_change_reason: assigneeReason || undefined,
        completion_reason: completionReasonText || undefined,
        due_date_change_reason: dueDateReason || undefined,
        deferred_until: deferredUntilDate || actionData.deferred_until || undefined,
        deferral_reason: deferralReasonText || undefined
      };
      
      await axios.put(`${API}/actions/${id}`, updatePayload);
      toast.success('Action updated successfully');
      setEditing(false);
      setAssigneeChangeReason('');
      setCompletionReason('');
      setDueDateChangeReason('');
      setDeferredUntil('');
      setDeferralReason('');
      setPendingUpdate(null);
      fetchActionDetail();
      fetchAuditLog();
      fetchNotes(); // Refresh notes as reasons are added as notes
    } catch (error) {
      toast.error('Failed to update action: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAssigneeChangeConfirm = async () => {
    if (!assigneeChangeReason.trim()) {
      toast.error('Please provide a reason for the assignee change');
      return;
    }
    setShowAssigneeChangeDialog(false);
    
    // Check if also need due date reason
    const dueDateChanged = originalAction && pendingUpdate.due_date !== originalAction.due_date;
    if (dueDateChanged && !dueDateChangeReason) {
      setShowDueDateChangeDialog(true);
      return;
    }
    
    // Check if also need deferral info
    const statusChangedToDeferred = originalAction && 
      pendingUpdate.status === 'Deferred' && 
      originalAction.status !== 'Deferred';
    
    if (statusChangedToDeferred && !deferredUntil) {
      setShowDeferralDialog(true);
      return;
    }
    
    // Check if also need completion reason
    const statusChangedToCompleted = originalAction && 
      pendingUpdate.status === 'Completed' && 
      originalAction.status !== 'Completed';
    
    if (statusChangedToCompleted && !completionReason) {
      setShowCompletionDialog(true);
      return;
    }
    
    await performUpdate(pendingUpdate, assigneeChangeReason, completionReason, dueDateChangeReason, deferredUntil, deferralReason);
  };

  const handleDueDateChangeConfirm = async () => {
    if (!dueDateChangeReason.trim()) {
      toast.error('Please provide a reason for the due date change');
      return;
    }
    setShowDueDateChangeDialog(false);
    
    // Check if also need deferral info
    const statusChangedToDeferred = originalAction && 
      pendingUpdate.status === 'Deferred' && 
      originalAction.status !== 'Deferred';
    
    if (statusChangedToDeferred && !deferredUntil) {
      setShowDeferralDialog(true);
      return;
    }
    
    // Check if also need completion reason
    const statusChangedToCompleted = originalAction && 
      pendingUpdate.status === 'Completed' && 
      originalAction.status !== 'Completed';
    
    if (statusChangedToCompleted && !completionReason) {
      setShowCompletionDialog(true);
      return;
    }
    
    await performUpdate(pendingUpdate, assigneeChangeReason, completionReason, dueDateChangeReason, deferredUntil, deferralReason);
  };

  const handleDeferralConfirm = async () => {
    if (!deferredUntil) {
      toast.error('Please set a review date for the deferred action');
      return;
    }
    setShowDeferralDialog(false);
    await performUpdate(pendingUpdate, assigneeChangeReason, completionReason, dueDateChangeReason, deferredUntil, deferralReason);
  };

  const handleCompletionConfirm = async () => {
    if (!completionReason.trim()) {
      toast.error('Please provide a reason for marking this action as complete');
      return;
    }
    setShowCompletionDialog(false);
    await performUpdate(pendingUpdate, assigneeChangeReason, completionReason, dueDateChangeReason, deferredUntil, deferralReason);
  };

  const handleCancelReasonDialog = () => {
    setShowAssigneeChangeDialog(false);
    setShowCompletionDialog(false);
    setShowDueDateChangeDialog(false);
    setShowDeferralDialog(false);
    setAssigneeChangeReason('');
    setCompletionReason('');
    setDueDateChangeReason('');
    setDeferredUntil('');
    setDeferralReason('');
    setPendingUpdate(null);
    // Revert the action to original values
    setAction(originalAction);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to archive this action? It can be restored later from Admin Tools.')) return;
    
    try {
      await axios.delete(`${API}/actions/${id}`);
      toast.success('Action archived successfully');
      navigate('/actions');
    } catch (error) {
      toast.error('Failed to archive action: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      await axios.post(`${API}/notes`, {
        action_id: id,
        note: newNote
      });
      setNewNote('');
      toast.success('Note added');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.note);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleSaveNote = async (noteId) => {
    if (!editingNoteText.trim()) {
      toast.error('Note cannot be empty');
      return;
    }

    try {
      await axios.put(`${API}/notes/${noteId}`, { note: editingNoteText });
      toast.success('Note updated');
      setEditingNoteId(null);
      setEditingNoteText('');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to update note: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await axios.delete(`${API}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Linked Actions handlers
  const handleOpenLinkDialog = () => {
    fetchAllActions();
    setShowLinkDialog(true);
    setLinkSearchTerm('');
    setSelectedActionToLink(null);
    setSelectedLinkType('related');
  };

  const handleLinkAction = async () => {
    if (!selectedActionToLink) {
      toast.error('Please select an action to link');
      return;
    }

    try {
      await axios.post(`${API}/actions/${id}/links`, {
        target_action_id: selectedActionToLink.id,
        link_type: selectedLinkType
      });
      toast.success(`Linked to ${selectedActionToLink.reference}`);
      setShowLinkDialog(false);
      fetchLinkedActions();
      fetchAuditLog();
    } catch (error) {
      toast.error('Failed to link action: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUnlinkAction = async (targetActionId, targetRef) => {
    if (!window.confirm(`Remove link to ${targetRef}?`)) return;

    try {
      await axios.delete(`${API}/actions/${id}/links/${targetActionId}`);
      toast.success('Link removed');
      fetchLinkedActions();
      fetchAuditLog();
    } catch (error) {
      toast.error('Failed to remove link: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleOpenCreateLinkedDialog = () => {
    setShowCreateLinkedDialog(true);
    setNewLinkedAction({
      title: '',
      description: '',
      owner_id: '',
      priority: 'Medium',
      due_date: '',
      note: ''
    });
  };

  const handleCreateLinkedAction = async () => {
    if (!newLinkedAction.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!newLinkedAction.owner_id) {
      toast.error('Assignee is required');
      return;
    }

    try {
      const response = await axios.post(`${API}/actions/${id}/create-linked`, newLinkedAction);
      toast.success(`Created linked action ${response.data.action.reference}`);
      setShowCreateLinkedDialog(false);
      fetchLinkedActions();
      fetchNotes();
      fetchAuditLog();
    } catch (error) {
      toast.error('Failed to create linked action: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Filter actions for link dialog
  const filteredActionsForLink = allActions.filter(a => 
    a.reference.toLowerCase().includes(linkSearchTerm.toLowerCase()) ||
    a.title.toLowerCase().includes(linkSearchTerm.toLowerCase())
  );

  // Check if action is blocked by any incomplete actions
  const isBlocked = linkedActions.some(l => 
    l.link_type === 'blocked_by' && 
    !['Completed', 'Duplicate', 'Not Required'].includes(l.status)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!action) {
    return <div className="text-center py-12">Action not found</div>;
  }

  const owner = users.find(u => u.id === action.owner_id);
  const category = categories.find(c => c.id === action.category);

  return (
    <div className="space-y-6 fade-in" data-testid="action-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/actions">
          <Button variant="ghost" size="sm" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Actions
          </Button>
        </Link>
        <div className="flex space-x-3">
          {canEditAction && (
            <>
              <Button
                variant="outline"
                onClick={() => setEditing(!editing)}
                data-testid="edit-button"
              >
                {editing ? 'Cancel' : 'Edit'}
              </Button>
              {editing && (
                <Button
                  onClick={handleUpdate}
                  className="bg-gradient-to-r from-blue-500 to-green-500"
                  data-testid="save-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
              )}
            </>
          )}
          {canArchive && (
            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-orange-600 hover:bg-orange-50"
              data-testid="archive-button"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archive
            </Button>
          )}
        </div>
      </div>

      <Card className="card-hover">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-mono text-gray-500 bg-gray-100 px-3 py-1 rounded">
                {action.reference}
              </span>
              {editing ? (
                <Input
                  value={action.title}
                  onChange={(e) => setAction({...action, title: e.target.value})}
                  className="text-2xl font-bold mt-3"
                  data-testid="title-edit"
                />
              ) : (
                <CardTitle className="text-3xl mt-3">{action.title}</CardTitle>
              )}
            </div>
            <div className="flex flex-col space-y-2">
              <span className={`status-badge priority-${action.priority.toLowerCase()}`}>
                {action.priority}
              </span>
              <span className={`status-badge status-${action.status.toLowerCase().replace(' ', '-')}`}>
                {action.status}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-sm font-medium text-gray-600">Description</Label>
            {editing ? (
              <Textarea
                value={action.description}
                onChange={(e) => setAction({...action, description: e.target.value})}
                rows={4}
                className="mt-2"
                data-testid="description-edit"
              />
            ) : (
              <p className="mt-2 text-gray-700">{action.description}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-medium text-gray-600">Assigned To</Label>
              {editing ? (
                <Select
                  value={action.owner_id}
                  onValueChange={(value) => setAction({...action, owner_id: value})}
                >
                  <SelectTrigger className="mt-2" data-testid="owner-edit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {groupUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        <span className="flex items-center gap-2">
                          <span 
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: user.display_group_color || '#3B82F6' }}
                          />
                          {user.name}
                          <span 
                            className="text-xs px-1.5 py-0.5 rounded-full"
                            style={{ 
                              backgroundColor: user.display_group_color ? `${user.display_group_color}20` : '#3B82F620',
                              color: user.display_group_color || '#3B82F6'
                            }}
                          >
                            {user.display_group || 'Unknown'}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className={`mt-2 font-medium ${getOrgBadgeColor(owner)}`}>{formatUserDisplay(owner)}</p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-600">Category</Label>
              {editing ? (
                <Select
                  value={action.category}
                  onValueChange={(value) => setAction({...action, category: value})}
                >
                  <SelectTrigger className="mt-2" data-testid="category-edit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="mt-2 text-gray-900 font-medium">{category?.name || 'N/A'}</p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-600">Date Raised</Label>
              {editing ? (
                <Input
                  type="date"
                  value={toInputDateFormat(action.date_raised)}
                  onChange={(e) => setAction({...action, date_raised: e.target.value})}
                  className="mt-2"
                  data-testid="date-raised-edit"
                />
              ) : (
                <p className="mt-2 text-gray-900 font-medium">
                  {formatDateDisplay(action.date_raised)}
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-600">Due Date</Label>
              {editing ? (
                <Input
                  type="date"
                  value={toInputDateFormat(action.due_date)}
                  onChange={(e) => setAction({...action, due_date: e.target.value})}
                  className="mt-2"
                  data-testid="due-date-edit"
                />
              ) : (
                <p className="mt-2 text-gray-900 font-medium">
                  {formatDateDisplay(action.due_date)}
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-600">Priority</Label>
              {editing ? (
                <Select
                  value={action.priority}
                  onValueChange={(value) => setAction({...action, priority: value})}
                >
                  <SelectTrigger className="mt-2" data-testid="priority-edit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="mt-2">
                  <span className={`status-badge priority-${action.priority.toLowerCase()}`}>
                    {action.priority}
                  </span>
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-600">Status</Label>
              {editing ? (
                <Select
                  value={action.status}
                  onValueChange={(value) => {
                    // When status changes, default status_changed_date to today if not already set
                    const today = new Date().toISOString().split('T')[0];
                    setAction({
                      ...action, 
                      status: value,
                      status_changed_date: action.status_changed_date || today
                    });
                  }}
                >
                  <SelectTrigger className="mt-2" data-testid="status-edit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Deferred">Deferred</SelectItem>
                    <SelectItem value="Duplicate">Duplicate</SelectItem>
                    <SelectItem value="Not Required">Not Required</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="mt-2">
                  <span className={`status-badge status-${action.status.toLowerCase().replace(' ', '-')}`}>
                    {action.status}
                  </span>
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-600">Status Changed Date</Label>
              {editing ? (
                <Input
                  type="date"
                  value={toInputDateFormat(action.status_changed_date)}
                  onChange={(e) => setAction({...action, status_changed_date: e.target.value})}
                  className="mt-2"
                  data-testid="status-changed-date-edit"
                />
              ) : (
                <p className="mt-2 text-gray-900 font-medium">
                  {formatDateDisplay(action.status_changed_date)}
                </p>
              )}
            </div>

            {/* Show Review Date for Deferred actions */}
            {action.status === 'Deferred' && (
              <div>
                <Label className="text-sm font-medium text-purple-600">Review Date</Label>
                {editing ? (
                  <Input
                    type="date"
                    value={toInputDateFormat(action.deferred_until)}
                    onChange={(e) => setAction({...action, deferred_until: e.target.value})}
                    className="mt-2 border-purple-300"
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="deferred-until-edit"
                  />
                ) : (
                  <p className="mt-2 text-purple-700 font-medium">
                    {action.deferred_until ? formatDateDisplay(action.deferred_until) : 'Not set'}
                  </p>
                )}
                {action.deferral_reason && !editing && (
                  <p className="mt-1 text-sm text-gray-500 italic">
                    Reason: {action.deferral_reason}
                  </p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="card-hover">
        <CardHeader>
          <CardTitle>Progress Notes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleAddNote} className="space-y-3">
            <Textarea
              placeholder="Add a progress note..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={3}
              data-testid="note-input"
            />
            <Button
              type="submit"
              className="bg-gradient-to-r from-blue-500 to-green-500"
              data-testid="add-note-button"
            >
              Add Note
            </Button>
          </form>

          <div className="space-y-4 mt-6">
            {notes.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No notes yet</p>
            ) : (
              notes.map((note) => {
                const author = users.find(u => u.id === note.author_id);
                const canModify = note.author_id === user?.id || isAdmin;
                const isEditingThis = editingNoteId === note.id;

                return (
                  <div key={note.id} className={`border-l-4 border-blue-500 pl-4 py-2 ${isEditingThis ? 'bg-blue-50 rounded-r-lg pr-4' : ''}`} data-testid={`note-${note.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">{author?.name || 'Unknown'}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {new Date(note.created_at).toLocaleString('en-GB')}
                          {note.updated_at && note.updated_at !== note.created_at && (
                            <span className="italic"> (edited)</span>
                          )}
                        </span>
                        {canModify && !isEditingThis && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => handleEditNote(note)}
                              title="Edit note"
                              data-testid={`edit-note-${note.id}`}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteNote(note.id)}
                              title="Delete note"
                              data-testid={`delete-note-${note.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    {isEditingThis ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          rows={3}
                          className="w-full"
                          data-testid="edit-note-input"
                        />
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEditNote}
                          >
                            <X className="w-3 h-3 mr-1" />
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleSaveNote(note.id)}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid="save-note-btn"
                          >
                            <Save className="w-3 h-3 mr-1" />
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-700 whitespace-pre-wrap">{note.note}</p>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Linked Actions Section */}
      <Card className="card-hover">
        <Collapsible open={linksOpen} onOpenChange={setLinksOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-blue-500" />
                  Linked Actions
                  <span className="text-sm font-normal text-gray-500">({linkedActions.length})</span>
                  {isBlocked && (
                    <span className="flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                      <AlertTriangle className="w-3 h-3" />
                      Blocked
                    </span>
                  )}
                </div>
                {linksOpen ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4">
              {/* Action buttons */}
              {canEditAction && (
                <div className="flex gap-2 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenLinkDialog}
                    data-testid="link-existing-btn"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Link Existing Action
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenCreateLinkedDialog}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                    data-testid="create-linked-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Linked Action
                  </Button>
                </div>
              )}

              {linkedActions.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No linked actions</p>
              ) : (
                <div className="space-y-2">
                  {linkedActions.map((link) => {
                    const isClosed = ['Completed', 'Duplicate', 'Not Required'].includes(link.status);
                    return (
                      <div
                        key={link.action_id}
                        className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 ${
                          link.link_type === 'blocked_by' && !isClosed ? 'border-amber-300 bg-amber-50' : ''
                        } ${link.is_archived ? 'opacity-50' : ''}`}
                        data-testid={`linked-action-${link.action_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`text-xs px-2 py-1 rounded font-medium ${
                            link.link_type === 'blocks' ? 'bg-blue-100 text-blue-700' :
                            link.link_type === 'blocked_by' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {link.link_type === 'blocks' ? 'Blocks' :
                             link.link_type === 'blocked_by' ? 'Blocked By' : 'Related'}
                          </span>
                          <Link
                            to={`/actions/${link.action_id}`}
                            className="flex items-center gap-2 hover:text-blue-600"
                          >
                            <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">
                              {link.reference}
                            </span>
                            <span className="font-medium">{link.title}</span>
                          </Link>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-500">{link.owner_name}</span>
                          <span className={`status-badge status-${link.status?.toLowerCase().replace(' ', '-')}`}>
                            {link.status}
                          </span>
                          {canEditAction && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                              onClick={() => handleUnlinkAction(link.action_id, link.reference)}
                              title="Remove link"
                              data-testid={`unlink-${link.action_id}`}
                            >
                              <Unlink className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Link Existing Action Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Link to Existing Action</DialogTitle>
            <DialogDescription>
              Search and select an action to link with {action?.reference}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Link Type</Label>
              <Select value={selectedLinkType} onValueChange={setSelectedLinkType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blocks">Blocks (this action must complete first)</SelectItem>
                  <SelectItem value="blocked_by">Blocked By (waiting on selected action)</SelectItem>
                  <SelectItem value="related">Related (general association)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Search Actions</Label>
              <Input
                placeholder="Search by reference or title..."
                value={linkSearchTerm}
                onChange={(e) => setLinkSearchTerm(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="max-h-60 overflow-y-auto border rounded-lg">
              {filteredActionsForLink.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No actions found</p>
              ) : (
                filteredActionsForLink.slice(0, 20).map((a) => (
                  <div
                    key={a.id}
                    className={`p-2 border-b cursor-pointer hover:bg-blue-50 ${
                      selectedActionToLink?.id === a.id ? 'bg-blue-100' : ''
                    }`}
                    onClick={() => setSelectedActionToLink(a)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">
                        {a.reference}
                      </span>
                      <span className="text-sm">{a.title}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <span>{users.find(u => u.id === a.owner_id)?.name || 'Unassigned'}</span>
                      <span>•</span>
                      <span className={`status-badge status-${a.status?.toLowerCase().replace(' ', '-')}`}>
                        {a.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleLinkAction} 
              disabled={!selectedActionToLink}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:text-gray-500"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Link Actions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Linked Action Dialog */}
      <Dialog open={showCreateLinkedDialog} onOpenChange={setShowCreateLinkedDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Linked Action</DialogTitle>
            <DialogDescription>
              Create a new action that is blocked by {action?.reference}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                placeholder="Action title..."
                value={newLinkedAction.title}
                onChange={(e) => setNewLinkedAction({...newLinkedAction, title: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the action..."
                value={newLinkedAction.description}
                onChange={(e) => setNewLinkedAction({...newLinkedAction, description: e.target.value})}
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assignee *</Label>
                <Select
                  value={newLinkedAction.owner_id}
                  onValueChange={(value) => setNewLinkedAction({...newLinkedAction, owner_id: value})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select assignee..." />
                  </SelectTrigger>
                  <SelectContent>
                    {groupUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.display_group || 'Unknown'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select
                  value={newLinkedAction.priority}
                  onValueChange={(value) => setNewLinkedAction({...newLinkedAction, priority: value})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={newLinkedAction.due_date}
                onChange={(e) => setNewLinkedAction({...newLinkedAction, due_date: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Progress Note (optional)</Label>
              <Textarea
                placeholder="Add a note to the current action..."
                value={newLinkedAction.note}
                onChange={(e) => setNewLinkedAction({...newLinkedAction, note: e.target.value})}
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateLinkedDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateLinkedAction} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Create & Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignee Change Reason Dialog */}
      <Dialog open={showAssigneeChangeDialog} onOpenChange={(open) => !open && handleCancelReasonDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Assignee Change Reason
            </DialogTitle>
            <DialogDescription>
              You are reassigning this action from <strong>{users.find(u => u.id === originalAction?.owner_id)?.name || 'Unknown'}</strong> to <strong>{users.find(u => u.id === action?.owner_id)?.name || 'Unknown'}</strong>. 
              Please provide a reason for this change.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for Reassignment *</Label>
              <Textarea
                placeholder="e.g., Transferred to RMG as they are now dealing with this matter..."
                value={assigneeChangeReason}
                onChange={(e) => setAssigneeChangeReason(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="assignee-change-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelReasonDialog}>Cancel</Button>
            <Button onClick={handleAssigneeChangeConfirm} className="bg-blue-600 hover:bg-blue-700">
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion Reason Dialog */}
      <Dialog open={showCompletionDialog} onOpenChange={(open) => !open && handleCancelReasonDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Completion Reason
            </DialogTitle>
            <DialogDescription>
              You are marking <strong>{action?.reference}</strong> as Completed. 
              Please provide a formal reason for closure.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for Completion *</Label>
              <Textarea
                placeholder="e.g., All tasks completed and approved by HLRA to be closed..."
                value={completionReason}
                onChange={(e) => setCompletionReason(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="completion-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelReasonDialog}>Cancel</Button>
            <Button onClick={handleCompletionConfirm} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Due Date Change Reason Dialog */}
      <Dialog open={showDueDateChangeDialog} onOpenChange={(open) => !open && handleCancelReasonDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-500" />
              Due Date Change Reason
            </DialogTitle>
            <DialogDescription>
              You are changing the due date from <strong>{originalAction?.due_date || 'Not set'}</strong> to <strong>{action?.due_date || 'Not set'}</strong>. 
              Please provide a reason for this change.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for Due Date Change *</Label>
              <Textarea
                placeholder="e.g., Extended deadline agreed at committee meeting on 15/01..."
                value={dueDateChangeReason}
                onChange={(e) => setDueDateChangeReason(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="due-date-change-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelReasonDialog}>Cancel</Button>
            <Button onClick={handleDueDateChangeConfirm} className="bg-orange-600 hover:bg-orange-700">
              <Clock className="w-4 h-4 mr-2" />
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deferral Dialog */}
      <Dialog open={showDeferralDialog} onOpenChange={(open) => !open && handleCancelReasonDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-500" />
              Defer Action
            </DialogTitle>
            <DialogDescription>
              You are deferring <strong>{action?.reference}</strong>. 
              Please set a review date when this action should be reconsidered.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Review Date *</Label>
              <Input
                type="date"
                value={deferredUntil}
                onChange={(e) => setDeferredUntil(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1"
                data-testid="deferred-until-date"
              />
              <p className="text-xs text-gray-500 mt-1">
                When should this action be reviewed? This will appear in the Meeting Review Report.
              </p>
            </div>
            <div>
              <Label>Reason for Deferral (Optional)</Label>
              <Textarea
                placeholder="e.g., Waiting for external party response, budget approval pending..."
                value={deferralReason}
                onChange={(e) => setDeferralReason(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="deferral-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelReasonDialog}>Cancel</Button>
            <Button onClick={handleDeferralConfirm} className="bg-purple-600 hover:bg-purple-700">
              <Clock className="w-4 h-4 mr-2" />
              Defer Action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log - Collapsible */}
      <Card className="card-hover">
        <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <History className="w-5 h-5 text-gray-500" />
                  Change History
                  <span className="text-sm font-normal text-gray-500">({auditLog.length} entries)</span>
                </div>
                {historyOpen ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              {auditLog.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No history available</p>
              ) : (
                <div className="space-y-3">
                  {auditLog.map((log) => (
                    <div key={log.id} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50" data-testid={`audit-${log.id}`}>
                      <div className="mt-1">
                        {log.change_type === 'created' && <CheckCircle className="w-4 h-4 text-green-500" />}
                        {log.change_type === 'updated' && <RefreshCw className="w-4 h-4 text-blue-500" />}
                        {log.change_type === 'archived' && <Archive className="w-4 h-4 text-orange-500" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            log.change_type === 'created' ? 'bg-green-100 text-green-700' :
                            log.change_type === 'updated' ? 'bg-blue-100 text-blue-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {log.change_type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">
                          <strong>{log.user_name}</strong>
                          {log.change_type === 'updated' && log.changes && (
                            <span className="text-gray-500">
                              {' changed '}
                              {Object.entries(log.changes).map(([field, values], idx) => (
                                <span key={field}>
                                  {idx > 0 && ', '}
                                  <em>{field}</em>
                                  {values.old && values.new && (
                                    <span className="text-xs"> ({String(values.old).substring(0, 20)} → {String(values.new).substring(0, 20)})</span>
                                  )}
                                </span>
                              ))}
                            </span>
                          )}
                          {log.change_type === 'created' && ' created this action'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {new Date(log.timestamp).toLocaleString('en-GB')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  );
}
